/* ============================================================================
   M4 — Courier Zone Mapping  (route /supply/zone-mapping)
   Wired to /api/zonemap (proship_mongo ZONE_MAPPING_API_DOC.md).
     1. Parent courier → ParentBasePanel: base status, register an S3 upload /
        copy an account (no base yet), Resync from S3 (base ready). Parent-wide.
     2. Account → ZoneSetupBody: overrides (pincode > city > state; each row
        Live / Not published / Removal not published), test a lane, and the
        Publish footer (dry run → confirm → incremental or full ZONE_PUBLISH job).
   Accounts that never publish read the parent base directly (no own folder).
   ========================================================================== */
import React, {useState, useEffect, useCallback, useMemo, useRef} from 'react'
import PropTypes from 'prop-types'
import {useSearchParams} from 'react-router-dom'
import {Box, Stack, Typography, Button, TextField, Chip, Paper, LinearProgress, IconButton, ToggleButtonGroup, ToggleButton, InputAdornment, Collapse, Tooltip} from '@mui/material'
import {alpha} from '@mui/material/styles'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import AddIcon from '@mui/icons-material/Add'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import CheckIcon from '@mui/icons-material/Check'
import UploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined'
import AutorenewIcon from '@mui/icons-material/Autorenew'
import SearchIcon from '@mui/icons-material/Search'
import CloseIcon from '@mui/icons-material/Close'
import PageTitle from '../../../components/PageTitle'
import {Badge, ConfirmDialog, SearchSelect, SupplyHero, useToast} from '../components'
import {CARD, Eyebrow, MONO, NO_P_MARGIN, PAGE_MAX, digitsOnly} from '../../merchant-zones/zoneUi'
import MapOutlinedIcon from '@mui/icons-material/MapOutlined'
import {apiError, normView, normJob} from '../supplyMeta'
import {
  useLazySearchCouriersCachedQuery,
  useLazyZoneSourcesQuery,
  useLazyZoneGetSetupQuery,
  useLazyZoneParentBaseQuery,
  useLazyZonePincodeOptionsQuery,
  useLazyZoneResolveCountQuery,
  useLazyZoneTestQuery,
  useLazyZoneJobQuery,
  useZoneAddOverrideMutation,
  useZoneDeleteOverrideMutation,
  useZoneSeedParentBaseMutation,
  useZoneResyncParentMutation,
  useZonePublishMutation,
} from '../../../redux/supplyApiSlice'
import {normalizeCourier} from '../m1-codes/codeData'

/* ───────── zone meta (verbatim colour ramp from the mockup) ───────── */
const ZN_META = [
  [1, 'LOCAL', '#2E7D32'],
  [2, 'REGIONAL', '#0288D1'],
  [3, 'METRO', '#1976D2'],
  [4, 'ROI', '#6A1B9A'],
  [5, 'SPECIAL', '#B7791F'],
]
/** Zone meta row for an override's zone (1-5); anything else falls back to Local. */
const znMeta = (z) => ZN_META[(z || 1) - 1] || ZN_META[0]
const ORDER = {PIN: 1, CITY: 2, STATE: 3}
const MODES = [['PIN', 'Pincodes'], ['CITY', 'Cities'], ['STATE', 'States']]
/** Singular mode label for an override row. */
const MODE_LABEL = {PIN: 'Pincode', CITY: 'City', STATE: 'State'}

/** HTTP 409 from an RTK mutation: another job is already running. */
const isConflict = (e) => e?.status === 409 || e?.data?.status === 409

/* Override ids pricing uses right now. Exact when the backend sends liveOverrideIds; when it doesn't
   (older backend), fall back to the publish hash: published and nothing changed ⇒ all live. */
const liveIdsOf = (view) =>
  Array.isArray(view?.liveOverrideIds)
    ? view.liveOverrideIds
    : !view?.hasUnpublishedChanges && view?.lastPublishedAt
      ? (view?.overrides || []).map((o) => o.id)
      : []

/* Duplicate / conflict detection for a new override against the saved ones of the same pickup.
   Same pickup + same mode + same drop set + same zone → duplicate. Same pickup + same mode with
   any overlapping drop → conflict (the resolver would silently take the first match), so the
   user must remove the other rule or trim the drops. Returns a message, or null when clean. */
function findDuplicateOverride(existing, pickup, mode, drops, zone) {
  const norm = (s) => String(s || '').trim().toLowerCase()
  const mine = new Set(drops.map(norm))
  for (const o of existing || []) {
    if (o.deleted || String(o.pickup) !== String(pickup) || o.mode !== mode) continue
    const theirs = (o.drops || []).map(norm)
    const overlap = theirs.filter((d) => mine.has(d))
    if (!overlap.length) continue
    const sameSet = overlap.length === mine.size && theirs.length === mine.size
    if (sameSet && Number(o.zone) === Number(zone)) return 'This override already exists for pickup ' + pickup + '.'
    if (sameSet) return 'Pickup ' + pickup + ' already has this exact drop set in zone ' + o.zone + ' — remove that rule first.'
    return 'Pickup ' + pickup + ' already has a ' + mode.toLowerCase() + ' rule covering ' + overlap.slice(0, 3).join(', ') + (overlap.length > 3 ? ' +' + (overlap.length - 3) + ' more' : '') + ' (zone ' + o.zone + '). Remove it or drop those from this rule.'
  }
  return null
}
const BLD_BLANK = {pickup: '', mode: 'PIN', pins: '', drops: [], zone: null}
const PIN_RE = /^\d{6}$/

/* ── one sizing system for the whole page ─────────────────────────────────
   Every field / select / toggle is 40px; a button beside fields is 40px too (BTN_ROW);
   a button in a text row, header or footer is 34px (BTN). Labels are a fixed 18px line. */
const CTRL_H = 40
const BTN_BASE = {textTransform: 'none', fontWeight: 700, borderRadius: '10px', whiteSpace: 'nowrap', flexShrink: 0}
const BTN = {...BTN_BASE, height: 34, px: 1.75}
const BTN_ROW = {...BTN_BASE, height: CTRL_H, px: 2}
const FIELD = {'& .MuiInputBase-root': {height: CTRL_H, fontSize: 13}, '& .MuiInputBase-input': {py: 0}}
const SELECT = {'& .MuiOutlinedInput-root': {height: CTRL_H, py: '0 !important', fontSize: 13}}
const LABEL = {mb: 0.75, height: 18, lineHeight: '18px', whiteSpace: 'nowrap'}

/* Numbered step header: "① Title — one-line hint" with an optional status node on the right.
   Every section of the setup body uses it so the page reads top-to-bottom as a procedure. */
function StepHeader({n, title, hint, right, sx}) {
  // steps after the first sit below a hairline so the single card reads as one procedure
  const divided = String(n) !== '1'
  return (
    <Stack className='zm-flush' direction='row' alignItems='flex-start' spacing={1.25}
      sx={{mb: 1.25, ...(divided ? {mt: 2.5, pt: 2.5, borderTop: 1, borderColor: 'divider'} : {}), ...sx}}>
      <Box sx={{width: 22, height: 22, borderRadius: '7px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11.5, fontWeight: 800, bgcolor: 'primary.main', color: '#fff'}}>
        {n}
      </Box>
      <Box sx={{flex: 1, minWidth: 0}}>
        <Typography sx={{fontSize: 13.5, fontWeight: 700, color: 'text.primary', lineHeight: '22px'}}>{title}</Typography>
        {hint ? <Typography sx={{fontSize: 12, color: 'text.secondary', mt: 0.25}}>{hint}</Typography> : null}
      </Box>
      {/* centred against title + hint, not pinned to the title's top edge */}
      {right ? <Box sx={{flexShrink: 0, alignSelf: 'center'}}>{right}</Box> : null}
    </Stack>
  )
}
StepHeader.propTypes = {n: PropTypes.node, title: PropTypes.node, hint: PropTypes.node, right: PropTypes.node, sx: PropTypes.object}

/* Layout rule for the step card: StepHeaders (and anything marked .zm-flush) stay flush left; every
   other direct child is indented by badge width + gap (22px + 10px) so content lines up under the
   step title. Fragments render no DOM, so conditional sections are covered too. */
const STEP_FLOW = {'& > :not(.zm-flush)': {ml: {xs: 0, md: '32px'}}}

/* Bordered panel (the new-override form). */
const STEP_BOX = {borderRadius: 2.5, p: 2, border: 1, borderColor: 'divider', bgcolor: 'background.paper'}

/* "Label value" fact for the status strip — a compact alternative to explainer tiles. */
const FACT_TONE = {ok: 'success.main', warn: 'warning.main', bad: 'error.main'}
function Fact({label, value, tone}) {
  return (
    <Stack direction='row' spacing={0.75} alignItems='baseline'>
      <Typography sx={{fontSize: 11, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', color: 'text.disabled'}}>{label}</Typography>
      <Typography sx={{fontSize: 12.5, fontWeight: 600, color: tone ? FACT_TONE[tone] : 'text.primary'}}>{value}</Typography>
    </Stack>
  )
}
Fact.propTypes = {label: PropTypes.node, value: PropTypes.node, tone: PropTypes.oneOf(['ok', 'warn', 'bad'])}

function SectionLabel({children, sx}) {
  return (
    <Typography sx={{fontSize: 11, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', color: 'text.secondary', ...sx}}>
      {children}
    </Typography>
  )
}
SectionLabel.propTypes = {children: PropTypes.node, sx: PropTypes.object}

function ZnBadge({z}) {
  const code = Number(z)
  // Courier files can hold codes outside the 5 standard zones (or 0) — show them as they are,
  // never as "Local".
  if (!(code >= 1 && code <= ZN_META.length)) {
    return (
      <Chip
        size='small'
        label={z == null ? 'No zone' : 'Zone ' + z}
        sx={{height: 22, fontSize: 11, fontWeight: 700, color: '#475569', bgcolor: alpha('#475569', 0.08), border: 1, borderColor: alpha('#475569', 0.3)}}
      />
    )
  }
  const m = ZN_META[code - 1]
  return (
    <Chip
      size='small'
      label={'Z' + m[0] + ' · ' + m[1]}
      sx={{height: 22, fontSize: 11, fontWeight: 700, color: m[2], bgcolor: alpha(m[2], 0.1), border: 1, borderColor: alpha(m[2], 0.35)}}
    />
  )
}
ZnBadge.propTypes = {z: PropTypes.number}

const parsePins = (s) =>
  String(s || '')
    .split(',')
    .map((x) => x.trim())
    .filter((x) => PIN_RE.test(x))

/* ════════════════════════════════════════════════════════════════════════
   Top-level — account-code picker + per-code editor
   ════════════════════════════════════════════════════════════════════════ */
export default function ZoneMapping() {
  const toast = useToast()
  const [params, setParams] = useSearchParams()
  const [code, setCode] = useState(() => params.get('code') || '')
  const [parent, setParent] = useState('')
  const [dirty, setDirty] = useState(false) // current account has unpublished override changes
  const [baseRev, setBaseRev] = useState(0) // bumped when the parent base changes → account view reloads
  const leaveOk = () =>
    !dirty || window.confirm('This account has override changes that are saved but NOT published, so pricing does not use them yet. Leave anyway?')

  const [codes, setCodes] = useState([])
  const [codesLoading, setCodesLoading] = useState(true)
  const [codesErr, setCodesErr] = useState('')
  const [fetchCouriers] = useLazySearchCouriersCachedQuery()

  // distinct parent couriers, and the codes under the chosen parent (cascade)
  const parents = useMemo(
    () => [...new Set(codes.map((c) => c.parent).filter(Boolean))].sort((a, b) => a.localeCompare(b)),
    [codes]
  )
  const visibleCodes = useMemo(() => (parent ? codes.filter((c) => c.parent === parent) : []), [codes, parent])

  // deep-link / reload: if ?code= is set, preselect its parent once the list loads
  useEffect(() => {
    if (code && !parent && codes.length) {
      const c = codes.find((x) => x.code === code)
      if (c && c.parent) setParent(c.parent)
    }
  }, [codes, code, parent])

  useEffect(() => {
    let on = true
    setCodesLoading(true)
    setCodesErr('')
    fetchCouriers({
      offset: 0,
      limit: 500,
      skipCount: true,
      andfilter: [{field: 'is_deleted', operator: 'eq', value: '0'}],
      includes: 'id,name,parent,cpAccountCode,prozoId,supportedShipmentType,type,serviceTypePrimary,serviceTypeSecondary,isActive',
    })
      .unwrap()
      .then((result) => {
        if (!on) return
        // /searchCached returns {data:[...]} (array); /search returns {data,count}. Handle both.
        const docs = Array.isArray(result) ? result : result?.data || []
        const rows = docs
          .map(normalizeCourier)
          .filter((c) => c.code)
          .sort((a, b) => (a.code || '').localeCompare(b.code || ''))
        setCodes(rows)
      })
      .catch((e) => on && setCodesErr(apiError(e)))
      .finally(() => on && setCodesLoading(false))
    return () => {
      on = false
    }
  }, [fetchCouriers])

  const onCode = (val) => {
    if (val !== code && !leaveOk()) return
    setDirty(false)
    setCode(val)
    const next = new URLSearchParams(params)
    if (val) next.set('code', val)
    else next.delete('code')
    setParams(next, {replace: true})
  }

  const onParent = (val) => {
    if (val !== parent && !leaveOk()) return
    setDirty(false)
    setParent(val)
    setCode('') // parent changed → clear the dependent code
    const next = new URLSearchParams(params)
    next.delete('code')
    setParams(next, {replace: true})
  }

  return (
    <>
      <PageTitle>Zone Mapping</PageTitle>

      {/* same blue hero as the other zone pages (Merchant Setup › Zone Mapping) */}
      <SupplyHero
        icon={<MapOutlinedIcon />}
        eyebrow={<Eyebrow>Courier Setup · Pricing &amp; Zones</Eyebrow>}
        title='Courier Zone Mapping'
        subtitle='The zone each courier account charges for a pickup → drop lane: the parent base plus account overrides.'
        sx={{maxWidth: PAGE_MAX}}
      />

      {/* the scoped supply stylesheet gives every <p> a 10px bottom margin, which pushes MUI Typography
          off-centre in flex rows (banner text, override rows). This card spaces things explicitly. */}
      <Box sx={{...CARD, maxWidth: PAGE_MAX, mb: 2, ...NO_P_MARGIN}}>
        <Box sx={{px: {xs: 2.5, md: 3}, py: 2.5, ...STEP_FLOW}}>
          {/* ── step 1: parent — its base (and Resync) belong here, not to any one account ── */}
          <StepHeader n='1' title='Parent courier' hint='Its base mapping is shared by every account under it.' />
          <Box sx={{maxWidth: 380}}>
            <SectionLabel sx={LABEL}>Parent courier *</SectionLabel>
            <SearchSelect
              sx={SELECT}
              value={parent}
              disabled={codesLoading || !!codesErr}
              placeholder={codesLoading ? 'Loading…' : codesErr ? 'Couldn’t load' : 'Search a parent…'}
              options={parents}
              onChange={(v) => onParent(v)}
            />
          </Box>
          {codesErr ? (
            <Stack direction='row' spacing={1} alignItems='flex-start' sx={{mt: 2, p: 1.5, borderRadius: 2, border: 1, borderColor: (th) => alpha(th.palette.error.main, 0.35), bgcolor: (th) => alpha(th.palette.error.main, 0.06), color: 'error.dark'}}>
              <InfoOutlinedIcon sx={{fontSize: 16, flexShrink: 0, mt: '1px', color: 'error.main'}} />
              <Typography sx={{fontSize: 12.5}}>{codesErr}</Typography>
            </Stack>
          ) : null}
          {parent ? (
            // keys are prefixed: a parent and an account code can share a name (e.g. PIKNDEL_UAT), and equal keys on
            // these sibling panels make React keep stale copies of the panel on every re-render
            <ParentBasePanel key={'parent:' + parent} parentName={parent} toast={toast} onBaseChange={() => setBaseRev((r) => r + 1)} />
          ) : null}

          {/* ── step 2: account — overrides, test and publish are per account ── */}
          <StepHeader n='2' title='Account' hint='Pick the account code to add overrides, test lanes and publish.' />
          <Box sx={{maxWidth: 380}}>
            <SectionLabel sx={LABEL}>Courier account code *</SectionLabel>
            <SearchSelect
              sx={SELECT}
              value={code}
              disabled={!parent || codesLoading || !!codesErr}
              placeholder={parent ? 'Search an account code…' : 'Select a parent first…'}
              options={visibleCodes.map((c) => ({value: c.code, label: c.display || c.code}))}
              onChange={(v) => onCode(v)}
            />
          </Box>
          {!code ? (
            <Typography sx={{fontSize: 12.5, color: 'text.disabled', mt: 2}}>
              Overrides, test and publish appear once you pick an account.
            </Typography>
          ) : (
            <ZoneSetupBody key={'code:' + code} code={code} toast={toast} onDirty={setDirty} baseRev={baseRev} />
          )}
        </Box>
      </Box>
    </>
  )
}

/* ════════════════════════════════════════════════════════════════════════
   Async city/state drop selector — typeahead backed by /pincodeOptions
   ════════════════════════════════════════════════════════════════════════ */
function AsyncDropSelect({mode, selected, onChange}) {
  const noun = mode === 'CITY' ? 'city' : 'state'
  const [q, setQ] = useState('')
  const [opts, setOpts] = useState([])
  const [loading, setLoading] = useState(false)
  const [open, setOpen] = useState(false)
  const [fetchPincodeOptions] = useLazyZonePincodeOptionsQuery()

  useEffect(() => {
    const term = q.trim()
    if (term.length < 3) {
      setOpts([])
      setLoading(false)
      return
    }
    let on = true
    setLoading(true)
    const t = setTimeout(() => {
      fetchPincodeOptions({mode, q: term})
        .unwrap()
        .then((r) => on && setOpts((Array.isArray(r) ? r : r?.data || []).slice(0, 50)))
        .catch(() => on && setOpts([]))
        .finally(() => on && setLoading(false))
    }, 280)
    return () => {
      on = false
      clearTimeout(t)
    }
  }, [q, mode, fetchPincodeOptions])

  const add = (name) => {
    if (selected.indexOf(name) < 0) onChange(selected.concat(name))
    setQ('')
    setOpts([])
  }
  const remove = (name) => onChange(selected.filter((s) => s !== name))
  const results = opts.filter((o) => selected.indexOf(o) < 0)

  return (
    <Box>
      <Box sx={{position: 'relative'}}>
        <TextField
          size='small'
          fullWidth
          placeholder={'Type to search ' + noun + 's (min 3 chars)…'}
          value={q}
          onChange={(e) => {
            setQ(e.target.value)
            setOpen(true)
          }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 160)}
          sx={FIELD}
          InputProps={{startAdornment: (<InputAdornment position='start'><SearchIcon sx={{fontSize: 17, opacity: 0.5}} /></InputAdornment>)}}
        />
        {open && q.trim().length >= 3 && (
          <Paper elevation={6} sx={{position: 'absolute', zIndex: 20, mt: 0.5, left: 0, right: 0, maxHeight: 220, overflowY: 'auto', borderRadius: 2}}>
            {loading ? (
              <Typography sx={{p: 1.5, fontSize: 12.5, color: 'text.secondary'}}>Searching…</Typography>
            ) : results.length ? (
              results.map((o) => (
                <Box
                  key={o}
                  component='button'
                  type='button'
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => add(o)}
                  sx={{display: 'block', width: '100%', textAlign: 'left', border: 'none', bgcolor: 'transparent', cursor: 'pointer', px: 1.5, py: 1, fontSize: 13, color: 'text.primary', '&:hover': {bgcolor: 'action.hover'}}}
                >
                  {o}
                </Box>
              ))
            ) : (
              <Typography sx={{p: 1.5, fontSize: 12.5, color: 'text.secondary'}}>No {noun}s match “{q.trim()}”.</Typography>
            )}
          </Paper>
        )}
      </Box>
      {selected.length ? (
        <Stack direction='row' spacing={0.75} flexWrap='wrap' useFlexGap sx={{mt: 1}}>
          {selected.map((s) => (
            <Chip key={s} size='small' label={s} onDelete={() => remove(s)} deleteIcon={<CloseIcon />} sx={{height: 24, fontSize: 12}} />
          ))}
        </Stack>
      ) : null}
    </Box>
  )
}
AsyncDropSelect.propTypes = {
  mode: PropTypes.oneOf(['CITY', 'STATE']).isRequired,
  selected: PropTypes.array.isRequired,
  onChange: PropTypes.func.isRequired,
}

/* Polls GET job/{id} while `job` ({id, progress}) is set: stores each poll's progress on it, clears it
   once the job is terminal and then calls onDone(progress, rawJob). Next poll 2.5s after a
   successful one, 4s after a failed one. Restarts only when the job id changes, so onDone is the
   one captured when polling started. */
function useJobPoller(job, setJob, onDone) {
  const [fetchJob] = useLazyZoneJobQuery()
  const id = job?.id
  useEffect(() => {
    if (!id) return undefined
    let on = true
    let timer = null
    const tick = () => {
      fetchJob(id)
        .unwrap()
        .then((raw) => {
          if (!on) return
          const p = normJob(raw)
          setJob((j) => (j ? {...j, progress: p} : j))
          if (p.terminal) {
            setJob(null)
            onDone(p, raw)
          } else {
            timer = setTimeout(tick, 2500)
          }
        })
        .catch(() => {
          if (on) timer = setTimeout(tick, 4000)
        })
    }
    tick()
    return () => {
      on = false
      if (timer) clearTimeout(timer)
    }
  }, [id]) // eslint-disable-line react-hooks/exhaustive-deps
}

/* ════════════════════════════════════════════════════════════════════════
   Parent base panel — shown as soon as a parent is picked. Base status, register
   an S3 upload / copy an account (when no base yet), Resync (when ready). All of
   this is parent-wide, so it never waits for an account selection.
   ════════════════════════════════════════════════════════════════════════ */
function ParentBasePanel({parentName, toast, onBaseChange}) {
  const [view, setView] = useState(null)
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState('')
  const [sources, setSources] = useState([])
  const [srcSel, setSrcSel] = useState('')
  const [seeding, setSeeding] = useState(false)
  const [resyncing, setResyncing] = useState(false)
  const [confirmResync, setConfirmResync] = useState(false)
  const [seedJob, setSeedJob] = useState(null) // {id, progress}

  const [fetchParentBase] = useLazyZoneParentBaseQuery()
  const [fetchSources] = useLazyZoneSourcesQuery()
  const [seedParentBaseReq] = useZoneSeedParentBaseMutation()
  const [resyncParentReq] = useZoneResyncParentMutation()

  const load = useCallback(() => {
    setErr('')
    return fetchParentBase(parentName)
      .unwrap()
      .then((raw) => {
        const v = normView(raw)
        setView(v)
        if (v.base.status === 'SEEDING' && v.parentBaseSeedJobId) {
          setSeedJob((j) => j || {id: v.parentBaseSeedJobId, progress: null}) // resume after reload
        }
        return v
      })
      .catch((e) => {
        setErr(apiError(e))
        return null
      })
  }, [parentName, fetchParentBase])

  useEffect(() => {
    setLoading(true)
    load().finally(() => setLoading(false))
  }, [load])

  // files missing in S3 = not ready: offer Register / copy instead of a Resync that would fail
  const ready = view?.base?.status === 'READY' && !view?.base?.filesMissing

  // source folders for "copy an account" — only needed while there is no base
  useEffect(() => {
    if (!view || ready) return undefined
    let on = true
    fetchSources()
      .unwrap()
      .then((r) => on && setSources(Array.isArray(r) ? r : r?.data || []))
      .catch(() => {})
    return () => {
      on = false
    }
  }, [view, ready, fetchSources])

  // seed job polling
  useJobPoller(seedJob, setSeedJob, (p, raw) => {
    const reason = raw?.metaDataObject?.lastError
    toast(p.failedJob ? 'Base seeding failed' + (reason ? ': ' + reason : '.') : 'Parent base is ready.', p.failedJob ? 'error' : 'success')
    load().then(() => onBaseChange && onBaseChange())
  })

  // resync progress — refresh while it runs, tell the account view when it ends
  const resyncRunning = !!view?.base?.resync?.running
  useEffect(() => {
    if (!resyncRunning) return undefined
    let on = true
    const timer = setInterval(() => {
      if (!on) return
      load().then((v) => {
        if (v && !v.base?.resync?.running) {
          const failed = v.base?.resync?.status === 'FAILED'
          toast(failed ? 'Parent resync finished with failures — check the account list.' : 'Parent resync complete — every published account is rebuilt.', failed ? 'warning' : 'success')
          if (onBaseChange) onBaseChange()
        }
      })
    }, 5000)
    return () => {
      on = false
      clearInterval(timer)
    }
  }, [resyncRunning]) // eslint-disable-line react-hooks/exhaustive-deps

  const seedBase = async () => {
    if (!srcSel) return toast('Pick a source account folder to seed the base from.', 'error')
    if (!view?.parentId) return toast('This parent has no entity on file — can’t seed a base.', 'error')
    setSeeding(true)
    try {
      const r = await seedParentBaseReq({parentId: view.parentId, sourceProzoId: srcSel}).unwrap()
      const jobId = r?.jobId || r?.id
      toast('Seeding parent base…', 'info')
      if (jobId) setSeedJob({id: jobId, progress: null})
      else await load()
    } catch (e) {
      toast(apiError(e), 'error')
    } finally {
      setSeeding(false)
    }
  }

  const resyncParent = async () => {
    setConfirmResync(false)
    if (!view?.parentId) return toast('This parent has no entity on file — nothing to resync.', 'error')
    setResyncing(true)
    try {
      const r = await resyncParentReq({parentId: view.parentId}).unwrap()
      toast(r?.message || 'Parent resync started.', 'info')
      await load()
      if (onBaseChange) onBaseChange()
    } catch (e) {
      if (isConflict(e)) toast('A resync or seed is already running for this parent.', 'warning')
      else toast(apiError(e), 'error')
    } finally {
      setResyncing(false)
    }
  }

  if (loading) {
    return <Typography sx={{fontSize: 12.5, color: 'text.secondary', mt: 2}}>Loading parent base…</Typography>
  }
  if (err || !view) {
    return (
      <Stack direction='row' spacing={1} alignItems='flex-start' sx={{mt: 2, color: 'error.dark'}}>
        <InfoOutlinedIcon sx={{fontSize: 16, mt: '1px'}} />
        <Typography sx={{fontSize: 12.5}}>{err || 'Couldn’t load the parent base.'}</Typography>
      </Stack>
    )
  }

  const v = view
  return (
    <Box sx={{mt: 2}}>
      <SectionLabel sx={LABEL}>Parent base</SectionLabel>
      {seedJob ? (
        <JobProgress kind='seed' progress={seedJob.progress} />
      ) : ready ? (
        <Box>
          {/* flex + spacer (not Stack spacing) so the button truly right-aligns */}
          <Box sx={{display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap'}}>
            <CheckIcon sx={{fontSize: 18, color: 'success.main'}} />
            <Typography sx={{fontWeight: 700, fontSize: 13}}>Ready</Typography>
            <Tooltip title={<Box component='span' sx={{fontFamily: MONO}}>parents/{v.parentId || '…'}/</Box>} arrow>
              <Typography sx={{fontSize: 12.5, color: 'text.secondary', borderBottom: '1px dotted', borderColor: 'text.disabled', cursor: 'help'}}>
                {v.base.fileCount.toLocaleString('en-IN')} pickup files
              </Typography>
            </Tooltip>
            <Typography sx={{fontSize: 12.5, color: 'text.secondary'}}>
              {v.base.seededFrom === 'DIRECT_UPLOAD' ? 'uploaded to S3' : v.base.seededFrom ? 'copied from ' + v.base.seededFrom : ''}
            </Typography>
            <Box sx={{flex: 1}} />
            {!v.base.resync?.running ? (
              <Button variant='outlined' startIcon={<AutorenewIcon />} disabled={resyncing} onClick={() => setConfirmResync(true)} sx={BTN}>
                {resyncing ? 'Starting…' : 'Resync from S3'}
              </Button>
            ) : null}
          </Box>
          {v.base.resync?.running ? <ResyncPanel resync={v.base.resync} /> : v.base.resync ? <Box sx={{mt: 0.75}}><ResyncSummary resync={v.base.resync} /></Box> : null}
        </Box>
      ) : (
        <Box>
          {v.base.status === 'FAILED' ? (
            <Stack direction='row' spacing={1} alignItems='flex-start' sx={{mb: 1.5, color: 'error.dark', fontSize: 12.5}}>
              <InfoOutlinedIcon sx={{fontSize: 16, mt: '1px'}} />
              <span><b>The last seed failed.</b> Pick one of the options below to set the base again.</span>
            </Stack>
          ) : v.base.filesMissing ? (
            <Stack direction='row' spacing={1} alignItems='flex-start' sx={{mb: 1.5, color: 'warning.dark', fontSize: 12.5}}>
              <InfoOutlinedIcon sx={{fontSize: 16, mt: '1px'}} />
              <span>
                <b>Base files are missing in S3.</b> Nothing is under{' '}
                <Box component='span' sx={{fontFamily: MONO}}>parents/{v.parentId || '…'}/</Box>. Upload the files and Register, or copy an account’s mapping.
              </span>
            </Stack>
          ) : null}
          <Stack spacing={1.25}>
            {/* option A — tech uploads the files to S3, then Register scans them. When the folder is known to be
                empty (filesMissing) the heading must not claim the files are there. */}
            <Stack direction={{xs: 'column', md: 'row'}} spacing={1.5} alignItems={{md: 'center'}}>
              <Box sx={{flex: 1, minWidth: 0}}>
                <Typography sx={{fontSize: 13, fontWeight: 700}}>
                  {v.base.filesMissing ? 'Upload the files to S3, then Register' : 'Files already in S3'}
                </Typography>
                <Typography sx={{fontSize: 12, color: 'text.secondary'}}>
                  One <Box component='span' sx={{fontFamily: MONO}}>{'{pickupPincode}.csv'}</Box> per pickup under{' '}
                  <Box component='span' sx={{fontFamily: MONO}}>parents/{v.parentId || '…'}/</Box>.
                  {v.base.filesMissing ? ' Once the files are there, ' : ' '}Register scans the folder and marks the base ready.
                </Typography>
              </Box>
              <Button variant='contained' disableElevation startIcon={<AutorenewIcon />} disabled={resyncing || !v.parentId} onClick={() => setConfirmResync(true)} sx={BTN_ROW}>
                {resyncing ? 'Scanning…' : 'Register'}
              </Button>
            </Stack>
            <Box sx={{display: 'flex', alignItems: 'center', gap: 1.5, color: 'text.disabled', fontSize: 11, fontWeight: 700, letterSpacing: '.08em', '&::before, &::after': {content: '""', flex: 1, borderTop: 1, borderColor: 'divider'}}}>OR</Box>
            {/* option B — copy another account's folder */}
            <Stack direction={{xs: 'column', md: 'row'}} spacing={1.5} alignItems={{md: 'center'}}>
              <Box sx={{flex: 1, minWidth: 0}}>
                <Typography sx={{fontSize: 13, fontWeight: 700}}>Copy another account’s mapping</Typography>
                <Typography sx={{fontSize: 12, color: 'text.secondary'}}>Copies that account’s zone files into the parent folder.</Typography>
              </Box>
              <SeedControl sources={sources} srcSel={srcSel} setSrcSel={setSrcSel} seeding={seeding} onSeed={seedBase} />
            </Stack>
          </Stack>
        </Box>
      )}

      <ConfirmDialog
        open={confirmResync}
        title={ready ? 'Resync parent to all accounts?' : 'Register the uploaded parent base?'}
        body={
          'Scans parents/' + (v.parentId || '…') + '/ in S3 and registers what is there as the ' + (v.parentName || 'parent') + ' base. ' +
          'Every account of this parent that was published from this page is then rebuilt on it, one at a time, keeping its own overrides. ' +
          'Accounts that were never published read the parent base directly and need nothing. Pickup files missing from the new upload are removed from the rebuilt accounts.'
        }
        okLabel={ready ? 'Start resync' : 'Register base'}
        onOk={resyncParent}
        onClose={() => setConfirmResync(false)}
      />
    </Box>
  )
}
ParentBasePanel.propTypes = {parentName: PropTypes.string.isRequired, toast: PropTypes.func.isRequired, onBaseChange: PropTypes.func}

function ZoneSetupBody({code, toast, onDirty, baseRev = 0}) {
  const [view, setView] = useState(null)
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState('')

  const [bld, setBld] = useState(BLD_BLANK)
  const [adding, setAdding] = useState(false)
  const [delId, setDelId] = useState(null)
  const [cnt, setCnt] = useState(null)

  const [tst, setTst] = useState({p: '', d: ''})
  const [testRes, setTestRes] = useState(null)
  const [testing, setTesting] = useState(false)

  const [builderOpen, setBuilderOpen] = useState(false) // rule form, collapsed once rules exist

  // Unpublished changes: warn on refresh/close, and tell the page so switching account asks first.
  // dirty = override adds/removes not yet published (a never-published account with no overrides is NOT dirty)
  const dirty = !!view && (
    (view.overrides || []).filter((o) => !liveIdsOf(view).includes(o.id)).length +
    (view.pendingRemovals || []).length) > 0
  useEffect(() => {
    if (onDirty) onDirty(dirty)
    if (!dirty) return undefined
    const warn = (e) => {
      e.preventDefault()
      e.returnValue = ''
    }
    window.addEventListener('beforeunload', warn)
    return () => window.removeEventListener('beforeunload', warn)
  }, [dirty, onDirty])

  const [job, setJob] = useState(null) // in-flight publish job {id, progress}
  const [dry, setDry] = useState(null)
  const [dryLoading, setDryLoading] = useState(false)
  const [publishing, setPublishing] = useState(false)
  const [confirmPublish, setConfirmPublish] = useState(false)
  const [publishFull, setPublishFull] = useState(false) // true = "Republish" (rebuild every file)

  const [fetchSetup] = useLazyZoneGetSetupQuery()
  const [fetchResolveCount] = useLazyZoneResolveCountQuery()
  const [fetchTest] = useLazyZoneTestQuery()
  const [addOverrideReq] = useZoneAddOverrideMutation()
  const [deleteOverrideReq] = useZoneDeleteOverrideMutation()
  const [publishReq] = useZonePublishMutation()

  /* ── load the setup view ─────────────────────────────────────────────── */
  const load = useCallback(() => {
    setErr('')
    return fetchSetup(code)
      .unwrap()
      .then((raw) => {
        const v = normView(raw)
        setView(v)
        // resume an in-flight publish advertised by the view (seeding is tracked by the parent panel)
        if (v.activePublishJobId) {
          setJob((j) => j || {id: v.activePublishJobId, progress: null})
        }
        return v
      })
      .catch((e) => setErr(apiError(e)))
  }, [code, fetchSetup])

  useEffect(() => {
    setLoading(true)
    load().finally(() => setLoading(false))
  }, [load])

  // The parent base changed (resync/seed started or finished): refresh the view in place. A remount
  // would wipe a half-typed override, test pincodes or an open confirm dialog.
  const firstBaseRev = useRef(baseRev)
  useEffect(() => {
    if (baseRev === firstBaseRev.current) return
    load()
  }, [baseRev, load])

  /* ── publish job polling (seeding is tracked by the parent panel) ────── */
  useJobPoller(job, setJob, (p, raw) => {
    const reason = raw?.metaDataObject?.lastError
    toast(p.failedJob ? 'Publish failed' + (reason ? ': ' + reason : ' — retry.') : 'Published — overrides are now live.', p.failedJob ? 'error' : 'success')
    load() // stay on the page; refresh so rows flip to "Live"
  })

  /* ── "resolves to N pincodes" for the builder drop set ───────────────── */
  const dropValues = useMemo(() => (bld.mode === 'PIN' ? parsePins(bld.pins) : bld.drops), [bld.mode, bld.pins, bld.drops])
  useEffect(() => {
    if (!dropValues.length) {
      setCnt(null)
      return
    }
    if (bld.mode === 'PIN') {
      setCnt(dropValues.length)
      return
    }
    let on = true
    const t = setTimeout(() => {
      fetchResolveCount({mode: bld.mode, values: dropValues})
        .unwrap()
        .then((r) => on && setCnt(typeof r === 'number' ? r : r?.count ?? r?.pincodes ?? null))
        .catch(() => on && setCnt(null))
    }, 300)
    return () => {
      on = false
      clearTimeout(t)
    }
  }, [bld.mode, dropValues, fetchResolveCount])

  /* ── live test resolution ────────────────────────────────────────────── */
  useEffect(() => {
    const valid = PIN_RE.test(tst.p) && PIN_RE.test(tst.d)
    if (!valid) {
      setTestRes(null)
      setTesting(false)
      return
    }
    let on = true
    setTesting(true)
    const t = setTimeout(() => {
      fetchTest({code, body: {pickup: tst.p, drop: tst.d}})
        .unwrap()
        .then((r) => on && setTestRes(r || {bad: 'No response from the resolver.'}))
        .catch((e) => on && setTestRes({bad: apiError(e)}))
        .finally(() => on && setTesting(false))
    }, 350)
    return () => {
      on = false
      clearTimeout(t)
    }
  }, [code, tst.p, tst.d, fetchTest])

  /* ── mutations ───────────────────────────────────────────────────────── */
  const addOverride = async () => {
    if (!PIN_RE.test(bld.pickup)) return toast('Enter a valid 6-digit pickup pincode.', 'error')
    const drops = dropValues
    if (!drops.length)
      return toast(bld.mode === 'PIN' ? 'Enter at least one valid drop pincode.' : 'Select at least one ' + (bld.mode === 'CITY' ? 'city' : 'state') + '.', 'error')
    if (!bld.zone) return toast('Pick the zone for this pickup–drop set.', 'error')
    // Duplicate / conflict check against the rules already saved for this pickup (the backend
    // enforces the same, this just gives instant feedback without a round trip).
    const dup = findDuplicateOverride(view?.overrides || [], bld.pickup, bld.mode, drops, bld.zone)
    if (dup) return toast(dup, 'error')
    setAdding(true)
    try {
      await addOverrideReq({code, body: {pickupPincode: bld.pickup, mode: bld.mode, drops, zone: bld.zone}}).unwrap()
      toast('Override saved — not live until you publish.', 'warning')
      setBld(BLD_BLANK)
      setCnt(null)
      await load()
    } catch (e) {
      toast(apiError(e), 'error')
    } finally {
      setAdding(false)
    }
  }

  const removeOverride = async () => {
    const id = delId
    setDelId(null)
    try {
      await deleteOverrideReq({code, overrideId: id}).unwrap()
      toast('Override removed — pricing keeps using it until you publish.', 'warning')
      await load()
    } catch (e) {
      toast(apiError(e), 'error')
    }
  }

  // Publish is one click: run the dry run quietly, then open the confirm dialog listing what will change.
  const openPublish = async (full = false) => {
    setPublishFull(!!full)
    setDryLoading(true)
    try {
      const r = await publishReq({code, dryRun: true, full: !!full}).unwrap()
      setDry(r || {})
    } catch (e) {
      setDry({warnings: ['Could not load the publish summary: ' + apiError(e)]})
    } finally {
      setDryLoading(false)
      setConfirmPublish(true)
    }
  }

  const runPublish = async () => {
    setConfirmPublish(false)
    setPublishing(true)
    try {
      const r = await publishReq({code, dryRun: false, full: publishFull}).unwrap()
      const jobId = r?.jobId || r?.id
      setDry(null)
      if (jobId) {
        toast('Publish started — copying base + overrides to S3.', 'info')
        setJob({id: jobId, progress: null})
      } else {
        toast('Published — overrides are now live.', 'success')
        await load() // stay on the page; refresh so every row flips to "Live"
      }
    } catch (e) {
      if (isConflict(e)) toast('A publish is already in flight for this account — wait for it to finish.', 'warning')
      else toast(apiError(e), 'error')
    } finally {
      setPublishing(false)
    }
  }

  /* ── render ──────────────────────────────────────────────────────────── */
  if (loading)
    return (
      <Box sx={{mt: 2}}>
        <Typography sx={{fontSize: 12.5, color: 'text.secondary'}}>Loading zone setup for {code}…</Typography>
      </Box>
    )
  if (err)
    return (
      <Box sx={{mt: 2}}>
        <Stack direction='row' spacing={1.25} alignItems='center' sx={{mb: 1.5, p: 1.5, borderRadius: 2, bgcolor: (th) => alpha(th.palette.error.main, 0.06), color: 'error.dark'}}>
          <InfoOutlinedIcon sx={{fontSize: 18}} />
          <Typography sx={{fontSize: 13}}>{err}</Typography>
        </Stack>
        <Button variant='outlined' onClick={() => load()} sx={BTN}>Retry</Button>
      </Box>
    )

  const v = view
  const baseReady = v.base.status === 'READY' && !v.base.filesMissing

  const overrides = v.overrides
    .slice()
    .sort((a, b) => (ORDER[a.mode] || 9) - (ORDER[b.mode] || 9))
  // live = in the last successful publish. Anything else is saved but pricing does NOT use it yet.
  const liveSet = new Set(liveIdsOf(v))
  const pendingRemovals = v.pendingRemovals || []
  const notLiveCount = overrides.filter((o) => !liveSet.has(o.id)).length
  const pendingCount = notLiveCount + pendingRemovals.length
  // needs publish = override changes pending, or the parent base changed after this account's last publish
  const needsPublish = pendingCount > 0 || (!!v.hasUnpublishedChanges && !!v.lastPublishedAt)

  // What the publish confirm lists — pending state from the view, new files + warnings from the dry run.
  // Publish confirm: what goes live / stops first, then one line on files, then caveats only if they apply.
  const lane = (o) => o.pickup + ' → ' + o.drops.slice(0, 2).join(', ') + (o.drops.length > 2 ? ' +' + (o.drops.length - 2) : '')
  const goingLive = overrides.filter((o) => !liveSet.has(o.id))
  const newFilePins = (dry && dry.pickupPinsMissingInBase) || []
  const dryWarnings = ((dry && dry.warnings) || []).filter((w) => !/no base file|not in the base/i.test(w))
  const filesToUpdate = dry && dry.filesToUpdate != null ? Number(dry.filesToUpdate) : null
  const changedPins = (dry && dry.changedPickupPins) || []
  const fileLine = !dry || !dry.mode
    ? null
    : dry.mode === 'DELTA'
      ? `${filesToUpdate} file${filesToUpdate === 1 ? '' : 's'} updated${changedPins.length > 1 ? ' (' + changedPins.slice(0, 5).join(', ') + (changedPins.length > 5 ? ' +' + (changedPins.length - 5) : '') + ')' : ''}, nothing else changes`
      : `Full rebuild: ${filesToUpdate != null ? filesToUpdate.toLocaleString('en-IN') + ' files' : 'every file'}${dry.fullReason ? ' (' + dry.fullReason.replace(/\.$/, '').toLowerCase() + ')' : ''}${filesToUpdate > 1000 ? ', may take several minutes' : ''}`
  const newFileLine = !newFilePins.length
    ? null
    : newFilePins.length === 1
      ? `${newFilePins[0]} is not in the parent base, so this file covers only the override; other drops from ${newFilePins[0]} stay unpriced`
      : `${newFilePins.slice(0, 5).join(', ')} are not in the parent base, so their files cover only the overrides`
  const publishBullets = [
    ...goingLive.slice(0, 3).map((o) => `${lane(o)} goes live as zone ${o.zone}`),
    ...(goingLive.length > 3 ? [`+${goingLive.length - 3} more overrides go live`] : []),
    ...pendingRemovals.slice(0, 3).map((o) => `${lane(o)} stops applying`),
    ...(pendingRemovals.length > 3 ? [`+${pendingRemovals.length - 3} more stop applying`] : []),
    ...(fileLine ? [fileLine] : []),
    ...(newFileLine ? [newFileLine] : []),
    ...dryWarnings,
  ]

  const countCopy =
    cnt != null
      ? 'Resolves to ' + cnt + ' pincode' + (cnt === 1 ? '' : 's')
      : ''

  return (
    <Box className='zm-flush' sx={{...STEP_FLOW, mt: 2}}>
      {/* account summary — base status lives in the parent panel above, so it isn't repeated here */}
      <Box sx={{display: 'flex', alignItems: 'center', flexWrap: 'wrap', columnGap: 3, rowGap: 1, px: 1.5, py: 1, borderRadius: 2, bgcolor: (th) => alpha(th.palette.primary.main, 0.035)}}>
        <Fact label='Pricing reads' value={v.inheritsParentFolder ? 'parent base' : 'this account'} />
        <Fact label='Overrides' value={pendingCount ? `${overrides.length - notLiveCount} live · ${pendingCount} pending` : `${overrides.length} live`} tone={pendingCount ? 'warn' : undefined} />
        <Box sx={{flex: 1}} />
        {baseReady ? (
          <Badge status={needsPublish ? 'pending' : v.lastPublishedAt ? 'active' : 'draft'} label={needsPublish ? 'Changes not live' : v.lastPublishedAt ? 'Published' : 'Never published'} />
        ) : (
          <Badge status={v.base.status === 'FAILED' ? 'failed' : 'pending'} label='Base needed' />
        )}
      </Box>

      {/* ── steps 3–4 + publish (locked until the parent base above is ready) ── */}
      {!baseReady ? (
        <Typography sx={{fontSize: 12.5, color: 'text.disabled', mt: 2}}>
          Overrides, test and publish unlock once the parent base above is ready.
        </Typography>
      ) : (
        <>
          <StepHeader
            n='3'
            title='Overrides'
            hint='Optional. Checked before the parent base; pincode beats city, city beats state.'
            right={overrides.length ? (
              <Button variant={builderOpen ? 'text' : 'outlined'} startIcon={<AddIcon />} onClick={() => setBuilderOpen((o) => !o)}
                sx={BTN}>
                {builderOpen ? 'Hide form' : 'Add override'}
              </Button>
            ) : null}
            sx={{mt: 2.5}}
          />

          {/* override builder (collapsed once rules exist) */}
          <Collapse in={builderOpen || !overrides.length}>
          <Box sx={{...STEP_BOX, bgcolor: (th) => alpha(th.palette.primary.main, 0.02), mb: 1.25}}>
            <SectionLabel sx={{mb: 1.5}}>New override</SectionLabel>
            {/* row 1: pickup · drop type · drops — every control 40px under an 18px label */}
            <Box sx={{display: 'flex', alignItems: 'flex-start', gap: 1.5, flexWrap: 'wrap', mb: 1.75}}>
              <Box>
                <SectionLabel sx={LABEL}>Pickup pincode</SectionLabel>
                <TextField
                  size='small'
                  value={bld.pickup}
                  placeholder='122002'
                  inputProps={{maxLength: 6, inputMode: 'numeric'}}
                  sx={{width: 150, ...FIELD, '& input': {fontFamily: MONO}}}
                  onChange={(e) => setBld((b) => ({...b, pickup: digitsOnly(e.target.value)}))}
                />
              </Box>
              <Box>
                <SectionLabel sx={LABEL}>Drops by</SectionLabel>
                <ToggleButtonGroup
                  size='small'
                  exclusive
                  value={bld.mode}
                  onChange={(e, val) => val && setBld((b) => ({...b, mode: val, pins: '', drops: []}))}
                  sx={{'& .MuiToggleButton-root': {height: CTRL_H, textTransform: 'none', fontWeight: 600, fontSize: 12.5, px: 1.5, py: 0}}}
                >
                  {MODES.map((s) => (
                    <ToggleButton key={s[0]} value={s[0]}>{s[1]}</ToggleButton>
                  ))}
                </ToggleButtonGroup>
              </Box>
              <Box sx={{flex: 1, minWidth: bld.mode === 'PIN' ? 220 : 300}}>
                <SectionLabel sx={LABEL}>
                  {bld.mode === 'PIN' ? 'Drop pincodes' : bld.mode === 'CITY' ? 'Cities' : 'States'}
                </SectionLabel>
                {bld.mode === 'PIN' ? (
                  <TextField
                    size='small'
                    fullWidth
                    value={bld.pins}
                    placeholder='Comma-separated · 122001, 110001, 201301'
                    sx={{...FIELD, '& input': {fontFamily: MONO}}}
                    onChange={(e) => setBld((b) => ({...b, pins: e.target.value}))}
                  />
                ) : (
                  <AsyncDropSelect mode={bld.mode} selected={bld.drops} onChange={(arr) => setBld((b) => ({...b, drops: arr}))} />
                )}
              </Box>
            </Box>
            {/* row 2: zone choice on the left, the action on the right — same 34px line */}
            <SectionLabel sx={LABEL}>Zone for this set</SectionLabel>
            <Box sx={{display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap'}}>
              <Stack direction='row' spacing={1} flexWrap='wrap' useFlexGap alignItems='center'>
                {ZN_META.map((m) => {
                  const on = bld.zone === m[0]
                  return (
                    <Chip
                      key={m[0]}
                      clickable
                      onClick={() => setBld((b) => ({...b, zone: m[0]}))}
                      label={
                        <Box component='span' sx={{display: 'inline-flex', alignItems: 'center', gap: 0.6}}>
                          <Box component='span' sx={{width: 17, height: 17, borderRadius: '5px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 800, bgcolor: on ? m[2] : alpha(m[2], 0.15), color: on ? '#fff' : m[2]}}>{m[0]}</Box>
                          {m[1]}
                        </Box>
                      }
                      sx={{height: 34, fontSize: 11.5, fontWeight: 700, color: m[2], border: 1, borderColor: on ? m[2] : alpha(m[2], 0.3), bgcolor: on ? alpha(m[2], 0.1) : 'background.paper', '&:hover': {bgcolor: alpha(m[2], 0.08)}}}
                    />
                  )
                })}
              </Stack>
              <Box sx={{flex: 1}} />
              {dropValues.length && countCopy ? <Typography sx={{fontSize: 12, color: 'text.secondary'}}>{countCopy}</Typography> : null}
              <Button variant='contained' disableElevation startIcon={<AddIcon />} disabled={adding} onClick={addOverride} sx={BTN}>
                {adding ? 'Adding…' : 'Add override'}
              </Button>
            </Box>
          </Box>
          </Collapse>

          {/* pending-changes banner: never let a saved override look active */}
          {pendingCount ? (
            <Box sx={{display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap', mb: 1.25, px: 1.5, py: 1, borderRadius: 2,
              border: 1, borderColor: (th) => alpha(th.palette.warning.main, 0.45), bgcolor: (th) => alpha(th.palette.warning.main, 0.08)}}>
              <InfoOutlinedIcon sx={{fontSize: 17, color: 'warning.dark'}} />
              <Typography sx={{fontSize: 12.5, color: 'warning.dark', fontWeight: 600}}>
                {pendingCount} override change{pendingCount === 1 ? ' is' : 's are'} saved but not live. Pricing still uses the last published version — publish below to apply.
              </Typography>
            </Box>
          ) : null}

          {/* override list — one sentence per rule, live state on every row, delete on hover */}
          {overrides.length || pendingRemovals.length ? (
            <Box sx={{borderTop: 1, borderColor: 'divider'}}>
              {overrides.map((o, i) => {
                const zm = znMeta(o.zone)
                const zName = zm[1].charAt(0) + zm[1].slice(1).toLowerCase()
                const live = liveSet.has(o.id)
                return (
                  <Box
                    key={o.id || i}
                    sx={{display: 'flex', alignItems: 'center', gap: 1.5, py: 1, pl: 1.25, borderBottom: 1, borderColor: 'divider',
                      borderLeft: '3px solid', borderLeftColor: live ? 'transparent' : 'warning.main',
                      bgcolor: live ? 'transparent' : (th) => alpha(th.palette.warning.main, 0.04),
                      '& .zm-del': {opacity: 0, transition: 'opacity .15s'}, '&:hover .zm-del, & .zm-del:focus-visible': {opacity: 1}}}
                  >
                    <Typography sx={{width: 64, flexShrink: 0, fontSize: 10.5, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', color: 'text.disabled'}}>
                      {MODE_LABEL[o.mode] || MODE_LABEL.STATE}
                    </Typography>
                    <Typography sx={{fontSize: 13, minWidth: 0, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>
                      From <Box component='span' sx={{fontFamily: MONO, fontWeight: 600}}>{o.pickup}</Box>
                      {' → '}
                      <Box component='span' sx={{fontFamily: o.mode === 'PIN' ? MONO : 'inherit'}}>{o.drops.slice(0, 3).join(', ')}</Box>
                      {o.drops.length > 3 ? <Box component='span' sx={{color: 'text.disabled'}}> +{o.drops.length - 3} more</Box> : null}
                    </Typography>
                    <Typography sx={{fontSize: 12.5, fontWeight: 700, color: zm[2], whiteSpace: 'nowrap'}}>Zone {zm[0]} · {zName}</Typography>
                    <Badge status={live ? 'active' : 'pending'} label={live ? 'Live' : 'Not published'} />
                    <IconButton className='zm-del' size='small' onClick={() => setDelId(o.id)} aria-label='Remove override'>
                      <DeleteOutlineIcon sx={{fontSize: 17}} />
                    </IconButton>
                  </Box>
                )
              })}
              {/* deleted but still in the published files — pricing keeps using these until publish */}
              {pendingRemovals.map((o, i) => {
                const zm = znMeta(o.zone)
                return (
                  <Box key={'rm-' + (o.id || i)}
                    sx={{display: 'flex', alignItems: 'center', gap: 1.5, py: 1, pl: 1.25, borderBottom: 1, borderColor: 'divider',
                      borderLeft: '3px solid', borderLeftColor: 'error.main', bgcolor: (th) => alpha(th.palette.error.main, 0.03)}}>
                    <Typography sx={{width: 64, flexShrink: 0, fontSize: 10.5, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', color: 'text.disabled'}}>
                      {MODE_LABEL[o.mode] || MODE_LABEL.STATE}
                    </Typography>
                    <Typography sx={{fontSize: 13, minWidth: 0, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'text.secondary', textDecoration: 'line-through'}}>
                      From <Box component='span' sx={{fontFamily: MONO}}>{o.pickup}</Box>
                      {' → '}
                      <Box component='span' sx={{fontFamily: o.mode === 'PIN' ? MONO : 'inherit'}}>{o.drops.slice(0, 3).join(', ')}</Box>
                      {o.drops.length > 3 ? <> +{o.drops.length - 3} more</> : null}
                    </Typography>
                    <Typography sx={{fontSize: 12.5, fontWeight: 700, color: 'text.disabled', whiteSpace: 'nowrap', textDecoration: 'line-through'}}>Zone {zm[0]}</Typography>
                    <Badge status='failed' label='Removal not published' />
                    <Box sx={{width: 34}} />
                  </Box>
                )
              })}
            </Box>
          ) : null}

          {/* step 4: test a lane */}
          <StepHeader n='4' title='Test a lane' sx={{mt: 2.5}} />
          {/* one line: labelled inputs, result inline on the right */}
          <Box sx={{display: 'flex', alignItems: 'flex-end', gap: 1.5, flexWrap: 'wrap'}}>
            <Box>
              <SectionLabel sx={LABEL}>Pickup</SectionLabel>
              <TextField size='small' placeholder='110001' inputProps={{maxLength: 6, inputMode: 'numeric'}} value={tst.p}
                sx={{width: 150, ...FIELD, '& input': {fontFamily: MONO}}} onChange={(e) => setTst((t) => ({...t, p: digitsOnly(e.target.value)}))} />
            </Box>
            <Box sx={{height: 40, display: 'flex', alignItems: 'center', color: 'text.disabled'}}><ArrowForwardIcon sx={{fontSize: 16}} /></Box>
            <Box>
              <SectionLabel sx={LABEL}>Drop</SectionLabel>
              <TextField size='small' placeholder='400001' inputProps={{maxLength: 6, inputMode: 'numeric'}} value={tst.d}
                sx={{width: 150, ...FIELD, '& input': {fontFamily: MONO}}} onChange={(e) => setTst((t) => ({...t, d: digitsOnly(e.target.value)}))} />
            </Box>
            <Box sx={{minHeight: 40, display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 220, fontSize: 12.5}}>
              {!(PIN_RE.test(tst.p) && PIN_RE.test(tst.d)) ? (
                <Typography sx={{fontSize: 12.5, color: 'text.disabled'}}>Enter both pincodes.</Typography>
              ) : testing ? (
                <Typography sx={{fontSize: 12.5, color: 'text.disabled'}}>Resolving…</Typography>
              ) : testRes && testRes.bad ? (
                <>
                  <InfoOutlinedIcon sx={{fontSize: 16, color: 'error.main'}} />
                  <Typography sx={{fontSize: 12.5, color: 'error.dark'}}>{testRes.bad}</Typography>
                </>
              ) : testRes ? (
                <>
                  <ZnBadge z={testRes.zone} />
                  {testRes.via ? <Typography sx={{fontSize: 12.5, color: 'text.secondary'}}>{testRes.via}</Typography> : null}
                  {testRes.overrideId && testRes.live === false ? (
                    <Badge status='pending' label='Not live — publish to apply' />
                  ) : null}
                </>
              ) : null}
            </Box>
          </Box>

          {/* publish footer — full card width, sticks to the viewport bottom while changes are pending */}
          <Box
            className='zm-flush'
            sx={{
              mt: 3,
              mx: {xs: -2.5, md: -3},
              mb: -2.5,
              px: {xs: 2.5, md: 3},
              py: 1.5,
              borderTop: 1,
              borderColor: 'divider',
              borderBottomLeftRadius: '12px',
              borderBottomRightRadius: '12px',
              bgcolor: needsPublish ? (th) => alpha(th.palette.warning.main, 0.1) : (th) => alpha(th.palette.primary.main, 0.025),
              ...(needsPublish ? {position: 'sticky', bottom: 0, zIndex: 2, boxShadow: '0 -6px 16px rgba(15,23,42,.08)'} : {}),
            }}
          >
            <Box sx={{display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap'}}>
              <Typography sx={{fontSize: 12.5, color: needsPublish ? 'warning.dark' : 'text.secondary', fontWeight: needsPublish ? 700 : 400}}>
                {needsPublish
                  ? pendingCount
                    ? `${pendingCount} change${pendingCount === 1 ? '' : 's'} not live — publish to apply them.`
                    : v.pendingPickupCount > 0 && !job
                      ? `The last publish did not finish — publish again to rewrite ${v.pendingPickupCount} pickup file${v.pendingPickupCount === 1 ? '' : 's'}.`
                      : 'Parent base changed since the last publish — publish to apply it.'
                  : v.lastPublishedAt
                    ? 'Last published ' + new Date(v.lastPublishedAt).toLocaleString('en-IN')
                    : 'Not published — pricing reads the parent base.'}
              </Typography>
              <Box sx={{flex: 1}} />
              {/* never hard-disabled when idle: "Republish" rebuilds the folder even when nothing looks pending
                  (e.g. after a backend change, or to repair files that an older publish skipped) */}
              <Button
                variant={needsPublish || !v.lastPublishedAt ? 'contained' : 'outlined'}
                disableElevation
                startIcon={<UploadOutlinedIcon />}
                disabled={publishing || dryLoading || !!job || !!bld.pickup.trim()}
                title={bld.pickup.trim() ? 'Finish or clear the new override form before publishing.' : !needsPublish && v.lastPublishedAt ? 'Nothing pending — republish rebuilds this account’s files from scratch.' : undefined}
                onClick={() => openPublish(!(needsPublish || !v.lastPublishedAt))}
                sx={BTN}
              >
                {dryLoading ? 'Checking…' : needsPublish || !v.lastPublishedAt ? 'Publish' : 'Republish'}
              </Button>
            </Box>
            {job && (
              <Box sx={{mt: 1.25}}>
                <JobProgress kind='publish' progress={job.progress} />
              </Box>
            )}
          </Box>
        </>
      )}

      {/* delete confirm */}
      <ConfirmDialog
        open={!!delId}
        title='Remove override?'
        body='Removed from this account’s overrides (kept in history). Pricing changes only after you publish.'
        okLabel='Remove'
        danger
        onOk={removeOverride}
        onClose={() => setDelId(null)}
      />

      {/* publish confirm */}
      <ConfirmDialog
        open={confirmPublish}
        title={'Publish ' + (v.prozoId || 'this account') + '?'}
        body='Takes effect when the background job finishes.'
        bullets={publishBullets}
        okLabel='Publish'
        onOk={runPublish}
        onClose={() => {
          setConfirmPublish(false)
          setDry(null)
        }}
      />
    </Box>
  )
}
ZoneSetupBody.propTypes = {code: PropTypes.string.isRequired, toast: PropTypes.func.isRequired, onDirty: PropTypes.func, baseRev: PropTypes.number}

/* ───────── parent resync: in-flight panel + last-run summary ───────── */
function ResyncPanel({resync}) {
  const r = resync || {}
  const total = (r.pending?.length || 0) + (r.done?.length || 0) + (r.failed?.length || 0) + (r.currentProzoId ? 1 : 0)
  const finished = (r.done?.length || 0) + (r.failed?.length || 0)
  const pct = total ? Math.round((finished / total) * 100) : null
  return (
    <Box sx={{mt: 1.5, borderRadius: 2, p: 1.5, border: 1, borderColor: (th) => alpha(th.palette.primary.main, 0.25), bgcolor: (th) => alpha(th.palette.primary.main, 0.05)}}>
      <SectionLabel sx={{mb: 1, color: 'primary.main'}}>Resyncing parent to all accounts</SectionLabel>
      <LinearProgress
        variant={pct == null ? 'indeterminate' : 'determinate'}
        value={pct == null ? undefined : pct}
        sx={{height: 8, borderRadius: 5, bgcolor: (th) => alpha(th.palette.primary.main, 0.12)}}
      />
      <Typography sx={{fontSize: 11.5, color: 'text.secondary', mt: 1}}>
        {finished} / {total} account{total === 1 ? '' : 's'} republished
        {r.currentProzoId ? <> · now publishing <Box component='span' sx={{fontFamily: MONO}}>{r.currentProzoId}</Box></> : null}
        {r.failed?.length ? ` · ${r.failed.length} failed` : ''}
        {' '}— one account at a time; safe to leave the page.
      </Typography>
      {r.skipped?.length ? (
        <Typography sx={{fontSize: 11.5, color: 'text.secondary', mt: 0.5}}>
          Skipped (hand-made folders, not touched): <Box component='span' sx={{fontFamily: MONO}}>{r.skipped.join(', ')}</Box>
        </Typography>
      ) : null}
    </Box>
  )
}
ResyncPanel.propTypes = {resync: PropTypes.object}

function ResyncSummary({resync}) {
  const r = resync || {}
  if (!r.status || r.running) return null
  const when = r.finishedAt ? new Date(r.finishedAt).toLocaleString('en-IN', {dateStyle: 'medium', timeStyle: 'short'}) : ''
  const failedTxt = r.failed?.length ? ` · ${r.failed.length} failed (${r.failed.join(', ')})` : ''
  const skippedTxt = r.skipped?.length ? ` · ${r.skipped.length} skipped` : ''
  return (
    <Typography sx={{fontSize: 11.5, color: r.status === 'FAILED' ? 'error.main' : 'text.secondary'}}>
      Last resync {r.status === 'FAILED' ? 'finished with failures' : 'completed'}
      {when ? ' ' + when : ''} · {r.done?.length || 0} republished{failedTxt}{skippedTxt}
      {r.by ? ' · by ' + r.by : ''}
    </Typography>
  )
}
ResyncSummary.propTypes = {resync: PropTypes.object}

/* ───────── seed control (source folder picker + seed button) ───────── */
function SeedControl({sources, srcSel, setSrcSel, seeding, onSeed}) {
  return (
    <Stack direction='row' spacing={1.25} alignItems='center' flexWrap='wrap' useFlexGap>
      <Box sx={{minWidth: 260}}>
        <SearchSelect
          sx={SELECT}
          value={srcSel}
          placeholder='Search a source folder…'
          options={sources.map((s) => {
            const pid = s.prozoId || s.prozo_id
            const fc = s.fileCount ?? s.file_count
            return {value: pid, label: pid + (fc != null ? ' · ' + fc + ' files' : '')}
          })}
          onChange={(v) => setSrcSel(v)}
        />
      </Box>
      <Button
        variant='contained'
        disableElevation
        startIcon={<AutorenewIcon />}
        disabled={seeding || !srcSel}
        onClick={onSeed}
        sx={BTN_ROW}
      >
        {seeding ? 'Seeding…' : 'Seed parent base'}
      </Button>
    </Stack>
  )
}
SeedControl.propTypes = {
  sources: PropTypes.array.isRequired,
  srcSel: PropTypes.string.isRequired,
  setSrcSel: PropTypes.func.isRequired,
  seeding: PropTypes.bool,
  onSeed: PropTypes.func.isRequired,
}

/* ───────── job progress bar (seed / publish) ───────── */
function JobProgress({kind, progress}) {
  const p = progress || {}
  const pct = p.pct == null ? null : p.pct
  return (
    <Box sx={{borderRadius: 2.5, p: 2, border: 1, borderColor: (th) => alpha(th.palette.primary.main, 0.25), bgcolor: (th) => alpha(th.palette.primary.main, 0.05)}}>
      <SectionLabel sx={{mb: 1, color: 'primary.main'}}>{kind === 'seed' ? 'Seeding parent base' : 'Publishing'}</SectionLabel>
      <LinearProgress
        variant={pct == null ? 'indeterminate' : 'determinate'}
        value={pct == null ? undefined : pct}
        sx={{height: 8, borderRadius: 5, bgcolor: (th) => alpha(th.palette.primary.main, 0.12)}}
      />
      <Typography sx={{fontSize: 11.5, color: 'text.secondary', mt: 1}}>
        {p.total
          ? `${(p.done || 0).toLocaleString('en-IN')} / ${p.total.toLocaleString('en-IN')} files`
          : 'Running in the background…'}
        {p.failed ? ` · ${p.failed} failed` : ''}
        {pct != null ? ` · ${pct}%` : ''}
        {' '}— this can take several minutes; safe to leave the page.
      </Typography>
    </Box>
  )
}
JobProgress.propTypes = {kind: PropTypes.string.isRequired, progress: PropTypes.object}

